import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import StatsCard from '../components/dashboard/StatsCard'
import { StatsCardSkeleton } from '../components/shared/Skeleton'
import ActivityFeed from '../components/dashboard/ActivityFeed'
import RecentItems from '../components/dashboard/RecentItems'
import QuickActions from '../components/dashboard/QuickActions'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import { dashboardService } from '../services/dashboardService'
import type { AdminStats, ProfessorStats, AlunoStats, Activity, RecentItem } from '../types/dashboard.types'

const Dashboard = () => {
  const { profile, userId } = useAuth()
  const [stats, setStats] = useState<AdminStats | ProfessorStats | AlunoStats | null>(null)
  const [activities, setActivities] = useState<Activity[]>([])
  const [recentItems, setRecentItems] = useState<RecentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (profile && userId) {
      fetchDashboardData()
    }
  }, [profile, userId])

  const fetchDashboardData = async () => {
    if (!profile || !userId) return

    try {
      setLoading(true)
      setError(null)

      // Buscar estatísticas baseadas no role
      let statsData
      if (profile.role === 'admin') {
        statsData = await dashboardService.getAdminStats()
      } else if (profile.role === 'professor') {
        statsData = await dashboardService.getProfessorStats(userId)
      } else {
        statsData = await dashboardService.getAlunoStats(userId)
      }

      setStats(statsData)

      // Buscar atividades recentes
      const activitiesData = await dashboardService.getRecentActivity(profile.role, userId)
      setActivities(activitiesData)

      // Buscar itens recentes
      const itemsData = await dashboardService.getRecentItems(profile.role, userId)
      setRecentItems(itemsData)

    } catch (err) {
      console.error('Erro ao carregar dashboard:', err)
      setError('Erro ao carregar dados do dashboard')
    } finally {
      setLoading(false)
    }
  }

  const getWelcomeMessage = () => {
    const hour = new Date().getHours()
    let greeting = 'Boa noite'
    if (hour < 12) greeting = 'Bom dia'
    else if (hour < 18) greeting = 'Boa tarde'

    return `${greeting}, ${profile?.full_name || 'Usuário'}!`
  }

  const getRoleLabel = () => {
    switch (profile?.role) {
      case 'admin': return 'Administrador'
      case 'professor': return 'Professor'
      case 'aluno': return 'Aluno'
      default: return 'Usuário'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" fullScreen={true} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 border border-red-200 p-4">
        <div className="text-sm text-red-700 font-medium">{error}</div>
        <button
          onClick={fetchDashboardData}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 md:p-12 shadow-bizu-lg text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl pointer-events-none" />
        
        <div className="relative z-10">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">{getWelcomeMessage()}</h1>
          <p className="text-primary-100 mt-4 text-lg md:text-xl font-medium max-w-2xl leading-relaxed">
            Status: <span className="text-secondary-400 font-black uppercase tracking-widest bg-secondary-500/10 px-2 py-1 rounded">{getRoleLabel()}</span>. 
            Mantenha o foco nos seus objetivos e acompanhe sua evolução em tempo real.
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Estatísticas Principais</h2>
        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <StatsCardSkeleton count={profile?.role === 'admin' ? 10 : profile?.role === 'professor' ? 6 : 7} />
          </div>
        ) : stats && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {profile?.role === 'admin' && 'totalUsers' in stats && (
              <>
                <StatsCard
                  title="Total de Usuários"
                  value={stats.totalUsers}
                  icon="users"
                  color="blue"
                  subtitle={`${stats.crescimentoMensal} novos este mês`}
                />
                <StatsCard
                  title="Administradores"
                  value={stats.totalAdmins}
                  icon="user-check"
                  color="red"
                />
                <StatsCard
                  title="Professores"
                  value={stats.totalProfessores}
                  icon="user-check"
                  color="green"
                />
                <StatsCard
                  title="Alunos"
                  value={stats.totalAlunos}
                  icon="user-check"
                  color="purple"
                />
                <StatsCard
                  title="Total de Aulas"
                  value={stats.totalAulas}
                  icon="academic-cap"
                  color="purple"
                />
                <StatsCard
                  title="Total de Testes"
                  value={stats.totalTestes}
                  icon="document-text"
                  color="indigo"
                />
                <StatsCard
                  title="Total de Questões"
                  value={stats.totalQuestoes}
                  icon="question-mark-circle"
                  color="yellow"
                />
                <StatsCard
                  title="Testes Realizados"
                  value={stats.testesRealizados}
                  icon="check-circle"
                  color="green"
                  subtitle={`${stats.taxaConclusaoMedia}% concluídos`}
                />
                <StatsCard
                  title="Notícias Publicadas"
                  value={stats.totalNoticias}
                  icon="newspaper"
                  color="blue"
                  subtitle={`${stats.noticiasFeatured} em destaque`}
                />
                <StatsCard
                  title="Usuários Ativos"
                  value={stats.usuariosAtivos}
                  icon="users"
                  color="green"
                  subtitle="Últimos 30 dias"
                />
              </>
            )}

            {profile?.role === 'professor' && 'minhasAulas' in stats && (
              <>
                <StatsCard
                  title="Minhas Aulas"
                  value={stats.minhasAulas}
                  icon="academic-cap"
                  color="purple"
                />
                <StatsCard
                  title="Meus Testes"
                  value={stats.meusTestes}
                  icon="document-text"
                  color="blue"
                />
                <StatsCard
                  title="Minhas Questões"
                  value={stats.minhasQuestoes}
                  icon="question-mark-circle"
                  color="yellow"
                />
                <StatsCard
                  title="Alunos Únicos"
                  value={stats.alunosUnicos}
                  icon="users"
                  color="green"
                />
                <StatsCard
                  title="Testes Realizados"
                  value={stats.testesRealizados}
                  icon="check-circle"
                  color="indigo"
                />
                <StatsCard
                  title="Taxa de Acerto Média"
                  value={`${stats.taxaAcertoMedia}%`}
                  icon="chart-bar"
                  color="green"
                />
              </>
            )}

            {profile?.role === 'aluno' && 'aulasDisponiveis' in stats && (
              <>
                <StatsCard
                  title="Aulas Disponíveis"
                  value={stats.aulasDisponiveis}
                  icon="academic-cap"
                  color="purple"
                />
                <StatsCard
                  title="Testes Disponíveis"
                  value={stats.testesDisponiveis}
                  icon="document-text"
                  color="blue"
                />
                <StatsCard
                  title="Meus Testes Realizados"
                  value={stats.meusTestesRealizados}
                  icon="check-circle"
                  color="green"
                />
                <StatsCard
                  title="Testes em Andamento"
                  value={stats.testesEmAndamento}
                  icon="clock"
                  color="yellow"
                />
                <StatsCard
                  title="Pontuação Média"
                  value={stats.pontuacaoMedia}
                  icon="star"
                  color="yellow"
                  subtitle={`Melhor: ${stats.melhorPontuacao}`}
                />
                <StatsCard
                  title="Taxa de Acerto"
                  value={`${stats.taxaAcerto}%`}
                  icon="chart-bar"
                  color="green"
                />
                <StatsCard
                  title="Tempo Médio"
                  value={`${stats.tempoMedioPorTeste}min`}
                  icon="clock"
                  color="indigo"
                  subtitle="Por teste"
                />
              </>
            )}
          </div>
        )}
      </section>

      {/* Quick Actions and Activity */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Atividade Recente</h2>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <QuickActions userRole={profile?.role} />
          <ActivityFeed activities={activities} loading={loading} />
        </div>
      </section>

      {/* Recent Items */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Itens Recentes</h2>
        <RecentItems items={recentItems} loading={loading} />
      </section>
    </div>
  )
}

export default Dashboard